import { Tree, SyntaxNodeRef, SyntaxNode } from '@lezer/common';

import { builtins } from './builtins.js';

import { has } from 'min-dash';

import {
  equals,
  isArray,
  getType,
  isDuration,
  isNumber,
  isContext,
  isBoolean
} from './types.js';

import {
  isRange,
  createRange
} from './range.js';

import {
  isInvocationFailure,
  wrapFunction
} from './function.js';

import {
  getFromContext
} from './utils.js';

import {
  parseExpression,
  parseUnaryTests
} from './parser.js';

import {
  isTemporal,
  addDuration,
  subtractTemporals,
  addDurations,
  toFeel
} from './temporal.js';


export type WarningType =
  | 'NO_VARIABLE_FOUND'
  | 'NO_CONTEXT_ENTRY_FOUND'
  | 'NO_PROPERTY_FOUND'
  | 'NOT_COMPARABLE'
  | 'INVALID_TYPE'
  | 'INVALID_ARGUMENTS'
  | 'NO_FUNCTION_FOUND'
  | 'FUNCTION_INVOCATION_FAILURE'
  | 'UNSUPPORTED';

export type SourceLocation = {
  from: number,
  to: number
};

export type Warning = {
  type: WarningType;
  message: string;
  position: SourceLocation;
  details: {
    template: string,
    values: Record<string, unknown>
  }
};

export type EvaluationResult<T> = {
  value: T;
  warnings: Warning[];
};

/**
 * Context passed to the interpreter as global variables.
 */
export type EvalContext = Record<string, unknown>;

export class SyntaxError extends Error {

  input: string;

  position: SourceLocation;

  constructor(
      message: string,
      details: {
        input: string,
        position: SourceLocation
      }
  ) {
    super(message);

    Object.assign(this, details);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatDetails(template: string, values: Record<string, any>) {

  return Object.keys(values).reduce((message, key) => {
    return message.replace(`{${key}}`, `'${ formatValue(values[key]) }'`);
  }, template);
}

class InterpreterContext {
  warnings: Warning[] = [];

  addWarning(node: Node, type: WarningType, details: { template: string, values: Record<string, unknown> }): void {

    this.warnings.push({
      type,
      message: formatDetails(details.template, details.values),
      details,
      position: node.position
    });
  }

  getWarnings(): Warning[] {
    return this.warnings;
  }

}

type Node = {
  name: string,
  input: string,
  position: SourceLocation
};

type StackEntry = {
  args: unknown[],
  node: Node
};

class Interpreter {

  _buildExecutionTree(tree: Tree, input: string, interpreterContext: InterpreterContext) {

    const root = {
      args: [],
      node: {
        name: '__ROOT',
        input,
        position: {
          from: 0,
          to: input.length
        }
      }
    };

    const stack: StackEntry[] = [ root ];

    tree.iterate({
      enter(nodeRef) {

        const {
          isError,
          isSkipped
        } = nodeRef.type;

        if (isError) {
          throw lintError(input, nodeRef);
        }

        if (isSkipped) {
          return false;
        }

        const {
          from,
          to,
          name
        } = nodeRef;

        stack.push({
          args: [],
          node: {
            name,

            // materialize the node text only when actually inspected
            // (operator dispatch, literals, error messages)
            get input() {
              return input.slice(from, to);
            },
            position: {
              from,
              to
            }
          }
        });
      },

      leave(nodeRef) {

        if (nodeRef.type.isSkipped) {
          return;
        }

        const {
          node,
          args
        } = stack.pop();

        const parent = stack[stack.length - 1];

        const expr = evalNode(node, args, interpreterContext);

        parent.args.push(expr);
      }
    });

    return {
      root: root.args[root.args.length - 1]
    };
  }

  evaluate(
      expression: string,
      evalContext: EvalContext,
      dialect: string | undefined,
      interpreterContext: InterpreterContext
  ) {

    const parseTree = parseExpression(expression, evalContext, dialect);

    const { root } = this._buildExecutionTree(parseTree, expression, interpreterContext);

    return {
      parseTree,
      root
    };
  }

  unaryTest(
      expression: string,
      evalContext: EvalContext,
      dialect: string | undefined,
      interpreterContext: InterpreterContext
  ) {

    const parseTree = parseUnaryTests(expression, evalContext, dialect);

    const { root } = this._buildExecutionTree(parseTree, expression, interpreterContext);

    return {
      parseTree,
      root
    };
  }

}

const interpreter = new Interpreter();

const TEMPORAL_TYPES = new Set([ 'date', 'time', 'date time', 'duration' ]);

const NUMBER_ONLY = [ 'number' ];

const FILTER_INDEX_TYPES = new Set([ 'number', 'boolean', 'any' ]);

const LIST_OR_RANGE = new Set([ 'list', 'range' ]);

export function unaryTest(
    expression: string,
    evalContext: EvalContext = {},
    dialect?: string
) : EvaluationResult<boolean | null> {

  const interpreterContext = new InterpreterContext();

  evalContext = coerceContext(evalContext);

  const value = evalContext['?'] !== undefined ? evalContext['?'] : null;

  const {
    root
  } = interpreter.unaryTest(expression, evalContext, dialect, interpreterContext);

  // root = fn(ctx) => test(val)
  const test = root(evalContext);

  const testResult = test(value);

  return {
    value: testResult,
    warnings: interpreterContext.getWarnings()
  };
}

export function evaluate(
    expression: string,
    evalContext: EvalContext = {},
    dialect?: string
): EvaluationResult<unknown> {

  const interpreterContext = new InterpreterContext();

  evalContext = coerceContext(evalContext);

  const {
    root
  } = interpreter.evaluate(expression, evalContext, dialect, interpreterContext);

  // root = Expression :: fn(ctx)

  const result = root(evalContext);

  return {
    value: result,
    warnings: interpreterContext.getWarnings()
  };
}

/**
 * Deep-coerce raw Temporal and JS Date values in a user context into
 * FEEL-native wrapper values, so that consumers may pass either flavor.
 */
function coerceContext(value) {

  if (isArray(value)) {
    return value.map(coerceContext);
  }

  if (isContext(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([ key, entry ]) => [ key, coerceContext(entry) ])
    );
  }

  return toFeel(value);
}


// eslint-disable-next-line @typescript-eslint/no-explicit-any
function evalNode(node: Node, args: any[], interpreterContext: InterpreterContext) {

  // resolve a raw function invocation result: emit the appropriate
  // warning for a failure signal and normalise it to `null`, so the
  // signal never leaks into an enclosing expression
  const resolveInvocation = (result) => {

    if (isInvocationFailure(result)) {
      interpreterContext.addWarning(node, result.warning as WarningType, {
        template: result.template,
        values: result.values
      });

      return null;
    }

    return result;
  };

  switch (node.name) {
  case 'ArithOp': return (context) => {

    const invalidType = (opName, left, right) => {
      interpreterContext.addWarning(node, 'INVALID_TYPE', {
        template: `Can't ${opName} {right} to {left}`,
        values: {
          left,
          right
        }
      });
    };

    const nullable = (op, opName, types = NUMBER_ONLY) => (a, b) => {

      const left = a(context);
      const right = b(context);

      if (isArray(left) || isArray(right)) {
        invalidType(opName, left, right);

        return null;
      }

      const leftType = getType(left);
      const rightType = getType(right);

      if (TEMPORAL_TYPES.has(leftType)) {
        if (!TEMPORAL_TYPES.has(rightType)) {
          invalidType(opName, left, right);

          return null;
        }
      } else if (leftType !== rightType || !types.includes(leftType)) {
        invalidType(opName, left, right);

        return null;
      }

      return op(left, right);
    };

    switch (node.input) {
    case '+': return nullable((a, b) => {

      // flip these as temporal + duration operations aren't commutative
      if (isDuration(a) && !isDuration(b)) {
        const tmp = a;
        a = b;
        b = tmp;
      }

      if (isTemporal(a) && isDuration(b)) {
        return addDuration(a, b, 1);
      } else if (isTemporal(a) && isTemporal(b)) {
        return null;
      } else if (isDuration(a) && isDuration(b)) {
        const result = addDurations(a, b, 1);

        if (result === null) {
          invalidType('add', a, b);
        }

        return result;
      }

      return a + b;
    }, 'add', [ 'string', 'number', 'date', 'time', 'duration', 'date time' ]);
    case '-': return nullable((a, b) => {
      if (isTemporal(a) && isDuration(b)) {
        return addDuration(a, b, -1);
      } else if (isTemporal(a) && isTemporal(b)) {
        return subtractTemporals(a, b);
      } else if (isDuration(a) && isDuration(b)) {
        const result = addDurations(a, b, -1);

        if (result === null) {
          invalidType('subtract', a, b);
        }

        return result;
      }

      return a - b;
    }, 'subtract', [ 'number', 'date', 'time', 'duration', 'date time' ]);
    case '*': return nullable((a, b) => a * b, 'multiply', [ 'number' ]);
    case '/': return nullable((a, b) => !b ? null : a / b, 'divide', [ 'number' ]);
    case '**':
    case '^': return nullable((a, b) => a ** b, 'exponentiate', [ 'number' ]);
    }
  };

  case 'CompareOp': return tag(() => {

    switch (node.input) {
    case '>': return (b) => createRange(b, null, false, false);
    case '>=': return (b) => createRange(b, null, true, false);
    case '<': return (b) => createRange(null, b, false, false);
    case '<=': return (b) => createRange(null, b, false, true);
    case '=': return (b) => (a) => equals(a, b);
    case '!=': return (b) => (a) => {
      const result = equals(a, b);
      return result === null ? null : !result;
    };
    }

  }, 'test');

  case 'BacktickIdentifier': return node.input.replace(/`/g, '');

  case 'Wildcard': return (_context) => true;

  case 'null': return tag((_context) => {
    return null;
  }, 'nil');

  case 'Disjunction': return tag((context) => {

    const left = args[0](context);
    const right = args[2](context);

    const a = typeof left === 'boolean' ? left : null;
    const b = typeof right === 'boolean' ? right : null;

    // FEEL three-valued or
    if (a === true || b === true) {
      return true;
    }

    if (a === false && b === false) {
      return false;
    }

    return null;
  }, 'test');

  case 'Conjunction': return tag((context) => {
    const left = args[0](context);
    const right = args[2](context);

    const a = typeof left === 'boolean' ? left : null;
    const b = typeof right === 'boolean' ? right : null;

    // FEEL three-valued and
    if (a === false || b === false) {
      return false;
    }

    if (a === true && b === true) {
      return true;
    }

    return null;
  }, 'test');

  case 'Context': {

    const entries = args.slice(1, -1);

    return (context) => {

      // entries may reference earlier keys, so evaluate each against
      // the outer context plus the keys collected so far
      const merged = { ...context };
      const result = {};

      for (const arg of entries) {
        const [ key, value ] = arg(merged);

        merged[key] = value;
        result[key] = value;
      }

      return result;
    };
  }

  case 'FunctionBody': return args[0];

  case 'FormalParameters': return args;

  case 'FormalParameter': return args[0];

  case 'ParameterName': return args.join(' ');

  case 'FunctionDefinition': return (context) => {
    const parameterNames = args[2];

    const external = args[4] === 'external';

    if (external) {
      interpreterContext.addWarning(node, 'UNSUPPORTED', {
        template: 'External functions are not supported',
        values: {}
      });

      return null;
    }

    const fnBody = args[external ? 5 : 4];

    return wrapFunction((...args) => {

      const fnContext = parameterNames.reduce((context, name, idx) => {

        // support positional parameters
        context[name] = args[idx];

        return context;
      }, { ...context });

      return fnBody(fnContext);
    }, parameterNames);
  };

  case 'ContextEntry': return (context) => {

    const key = typeof args[0] === 'function' ? args[0](context) : args[0];

    const value = args[1](context);

    return [ key, value ];
  };

  case 'Key': return args[0];

  case 'Identifier': return node.input;

  case 'SpecialFunctionName': {

    // builtins are fixed; resolve once at build time
    const fn = getBuiltin(node.input, null);

    return () => fn;
  }

  // preserve spaces in name, but compact multiple
  // spaces into one (token)
  case 'Name': return node.input.replace(/\s{2,}/g, ' ');

  case 'PathName': return tag((context) => {
    const name = args.join(' ');

    const contextValue = getFromContext(name, context);

    if (typeof contextValue !== 'undefined') {
      return contextValue;
    }

    if (isContext(context)) {
      interpreterContext.addWarning(node, 'NO_CONTEXT_ENTRY_FOUND', {
        template: `Key '${name}' not found in {target}`,
        values: {
          target: context
        }
      });
    } else {
      interpreterContext.addWarning(node, 'NO_PROPERTY_FOUND', {
        template: `Property '${name}' not found in {target}`,
        values: {
          target: context
        }
      });
    }

    return null;
  }, 'any');

  case 'VariableName': {

    const name = args.join(' ');

    // builtins are fixed; resolve the fallback once at build time
    const builtin = getBuiltin(name, null);

    return tag((context) => {
      const contextValue = getFromContext(name, context);

      if (typeof contextValue !== 'undefined') {
        return contextValue;
      }

      if (builtin) {
        return builtin;
      }

      interpreterContext.addWarning(node, 'NO_VARIABLE_FOUND', {
        template: `Variable '${name}' not found`,
        values: {}
      });

      return null;
    }, 'any');
  }

  case 'QualifiedName': return {
    kind: 'name',
    name: normalizeTypeName(node.input),

    // fall back to a user-provided constructor / value resolved from
    // context, preserving the historic `x instance of SomeClass` behavior
    resolve: (context) => args.reduce(
      (context, arg) => typeof arg === 'function' ? arg(context) : context,
      context
    )
  };

  case '?': return (context) => getFromContext('?', context);

  // expression
  // expression ".." expression
  case 'IterationContext': return (context) => {

    const a = args[0](context);

    const b = args[2] && args[2](context);

    return b ? createRange(a, b) : a;
  };

  case 'Type': return args[0];

  // (x in [ [1,2], [3,4] ]), (y in x)
  case 'InExpressions': return (context) => {

    // we build up evaluation contexts from left to right,
    // ending up with the cartesian product over all available contexts
    //
    // previous context is provided to later context providers
    // producing <null> as a context during evaluation causes the
    // whole result to turn <null>

    type Context = EvalContext;
    type MaybeContext = (Context | null);
    type ContextsProducer = (context: Context) => (MaybeContext[] | null);

    const isValidContexts = (
        contexts: MaybeContext[] | null
    ) => {
      if (contexts === null || contexts.some(arr => getType(arr) === 'nil')) {
        return false;
      }

      return true;
    };

    const join = (
        aContexts: EvalContext[],
        bContextProducer: ContextsProducer
    ) => {

      const result = [];

      for (const aContext of aContexts) {

        const bContexts = bContextProducer({ ...context, ...aContext });

        if (!isValidContexts(bContexts)) {
          return null;
        }

        for (const bContext of bContexts) {
          result.push({ ...aContext, ...bContext });
        }
      }

      return result;
    };

    const cartesian = (
        aContexts: MaybeContext[],
        bContextProducer?: ContextsProducer,
        ...otherContextProducers: ContextsProducer[]
    ) : (MaybeContext[] | null) => {

      if (!isValidContexts(aContexts)) {
        return null;
      }

      if (!bContextProducer) {
        return aContexts;
      }

      return cartesian(join(aContexts, bContextProducer), ...otherContextProducers);
    };

    const cartesianProduct = (contextProducers: ContextsProducer[]) => {

      const [ aContextProducer, ...otherContextProducers ] = contextProducers;

      const aContexts = aContextProducer(context);

      return cartesian(aContexts, ...otherContextProducers);
    };

    const product = cartesianProduct(args);

    return product && product.map(p => {
      return { ...context, ...p };
    });
  };

  // Name kw<"in"> Expr
  case 'InExpression': return (context) => {
    return extractValue(context, args[0], args[2]);
  };

  case 'SpecialType': return {
    kind: 'name',
    name: normalizeTypeName(node.input)
  };

  case 'ListType': return {
    kind: 'list',
    element: args.find(isTypeDescriptor)
  };

  case 'ContextType': return {
    kind: 'context',
    entries: args.find(Array.isArray) || []
  };

  case 'ContextEntryTypes': return args.filter(
    arg => arg && arg.name
  );

  case 'ContextEntryType': return {
    name: args[0],
    type: args.find(isTypeDescriptor)
  };

  case 'FunctionType': return {
    kind: 'function'
  };

  case 'InstanceOfExpression': return tag((context) => {

    const value = args[0](context);

    return matchesType(value, args[3], context);
  }, 'test');

  case 'every': return tag((context) => {
    return (_contexts, _condition) => {
      const contexts = _contexts(context);

      if (getType(contexts) !== 'list') {
        return contexts;
      }

      return contexts.every(ctx => isTruthy(_condition(ctx)));
    };

  }, 'test');

  case 'some': return tag((context) => {
    return (_contexts, _condition) => {
      const contexts = _contexts(context);

      if (getType(contexts) !== 'list') {
        return contexts;
      }

      return contexts.some(ctx => isTruthy(_condition(ctx)));
    };
  }, 'test');

  case 'NumericLiteral': {

    // parse once at build time, not per evaluation
    const value = node.input.includes('.') ? parseFloat(node.input) : parseInt(node.input);

    return constant(value, 'number');
  }

  case 'BooleanLiteral': {

    const value = node.input === 'true';

    return constant(value, 'boolean');
  }

  case 'StringLiteral': {

    const value = parseString(node.input);

    return constant(value, 'string');
  }

  case 'PositionalParameters': {

    // constant parameters: enable constant folding of the enclosing
    // invocation (e.g. date("2026-12-12"))
    if (args.every(isConstant)) {
      return constant(args.map(arg => arg.constantValue), 'parameters');
    }

    return (context) => args.map(arg => arg(context));
  }

  case 'NamedParameter': return (context) => {

    const name = args[0];
    const value = args[1](context);

    return [ name, value ];
  };

  case 'NamedParameters': return (context) => args.reduce((args, arg) => {
    const [ name, value ] = arg(context);

    args[name] = value;

    return args;
  }, {});

  case 'DateTimeConstructor':

    // date / time / date and time / duration are pure builtins, resolved
    // independent of the evaluation context
    return constant(getBuiltin(node.input, null), 'function');

  case 'DateTimeLiteral': {

    // fold constant constructor invocations, e.g. date("2026-12-12")
    if (args.length !== 1 && isConstant(args[0]) && isConstant(args[2])) {
      const wrappedFn = wrapFunction(args[0].constantValue);

      if (wrappedFn) {
        const result = wrappedFn.invoke(args[2].constantValue);

        // keep the runtime path (and its warning) on invocation failure
        if (!isInvocationFailure(result)) {
          return constant(result, 'date');
        }
      }
    }

    return tag((context) => {

      // AtLiteral
      if (args.length === 1) {
        return args[0](context);
      }

      // FunctionInvocation
      else {
        const target = args[0](context);
        const wrappedFn = wrapFunction(target);

        if (!wrappedFn) {
          interpreterContext.addWarning(node, 'NO_FUNCTION_FOUND', {
            template: 'Cannot invoke {target}',
            values: {
              target
            }
          });

          return null;
        }

        const contextOrArgs = args[2](context);

        const result = wrappedFn.invoke(contextOrArgs);

        return resolveInvocation(result);
      }

    }, 'date');
  }

  case 'AtLiteral': {

    const arg = args[0];

    // fold constant @"..." literals: parse once at build time
    if (isConstant(arg)) {
      const wrappedFn = wrapFunction(getBuiltin('@', null));

      if (wrappedFn) {
        const value = wrappedFn.invoke([ arg.constantValue ]);

        // keep the runtime path (and its warning) on invocation failure
        if (!isInvocationFailure(value)) {
          return constant(value, 'date');
        }
      }
    }

    // builtins are fixed; resolve and wrap '@' once at build time
    const wrappedFn = wrapFunction(getBuiltin('@', null));

    return tag((context) => {

      if (!wrappedFn) {
        interpreterContext.addWarning(node, 'NO_FUNCTION_FOUND', {
          template: "Cannot invoke '@'",
          values: {}
        });

        return null;
      }

      return resolveInvocation(wrappedFn.invoke([ arg(context) ]));
    }, 'date');
  }

  case 'FunctionInvocation': return tag((context) => {

    const target = args[0](context);

    const wrappedFn = wrapFunction(target);

    if (!wrappedFn) {
      interpreterContext.addWarning(node, 'NO_FUNCTION_FOUND', {
        template: 'Cannot invoke {target}',
        values: {
          target
        }
      });

      return null;
    }

    const contextOrArgs = args[2](context);

    const result = wrappedFn.invoke(contextOrArgs);

    return resolveInvocation(result);
  }, 'any');

  case 'IfExpression': return (function() {

    const ifCondition = args[1];

    const thenValue = args[3];
    const elseValue = args[5];

    const type = coalecenseTypes(thenValue, elseValue);

    return tag((context) => {

      if (isTruthy(ifCondition(context))) {
        return thenValue(context);
      } else {
        return elseValue ? elseValue(context) : null;
      }
    }, type);

  })();

  case 'Parameters': return args.length === 3 ? args[1] : (_context) => [];

  case 'Comparison': return tag((context) => {

    const operator = args[1];

    // expression !compare kw<"in"> PositiveUnaryTest |
    // expression !compare kw<"in"> !unaryTest "(" PositiveUnaryTests ")"
    if (operator === 'in') {
      return compareIn(args[0](context), (args[3] || args[2])(context));
    }

    // expression !compare kw<"between"> expression kw<"and"> expression
    if (operator === 'between') {

      const start = args[2](context);
      const end = args[4](context);

      if (start === null || end === null) {
        return null;
      }

      return createRange(start, end).includes(args[0](context));
    }

    // expression !compare CompareOp<"=" | "!="> expression |
    // expression !compare CompareOp<Gt | Gte | Lt | Lte> expression |
    const left = args[0](context);
    const right = args[2](context);

    const test = operator()(right);

    return compareValue(test, left);
  }, 'test');

  case 'QuantifiedExpression': return tag((context) => {

    const testFn = args[0](context);

    const contexts = args[1];

    const condition = args[3];

    return testFn(contexts, condition);
  }, 'test');

  // DMN 1.2 - 10.3.2.14
  // kw<"for"> commaSep1<InExpression<IterationContext>> kw<"return"> expression
  case 'ForExpression': return (context) => {
    const extractor = args[args.length - 1];

    const iterationContexts = args[1](context);

    if (getType(iterationContexts) !== 'list') {
      return iterationContexts;
    }

    const partial = [];

    for (const ctx of iterationContexts) {

      // iteration contexts are fresh objects (created by InExpressions),
      // so expose the partial results by mutation instead of copying
      // the context per iteration
      ctx.partial = partial;

      partial.push(extractor(ctx));
    }

    return partial;
  };

  case 'ArithmeticExpression': return (function() {

    // binary expression (a + b)
    if (args.length === 3) {
      const [ a, op, b ] = args;

      return tag((context) => {
        return op(context)(a, b);
      }, coalecenseTypes(a, b));
    }

    // unary expression (-b)
    if (args.length === 2) {
      const [ op, value ] = args;

      return tag((context) => {

        return op(context)(() => 0, value);
      }, value.type);
    }
  })();

  case 'PositiveUnaryTest': return (context) => {

    // ensure that we strictly compare boolean values
    // with the implicit context value, if we match a unary test
    if (args[0].type === 'boolean' && has(context, '?')) {
      return args[0](context) === context['?'];
    }

    return args[0](context);
  };

  case 'ParenthesizedExpression': return args[1];

  // expr "."" pathName
  case 'PathExpression': return tag((context) => {

    const pathTarget = args[0](context);

    // PathName
    const pathProp = args[2];

    if (isArray(pathTarget)) {
      return pathTarget.map(value => pathProp(value));
    } else {
      return pathProp(pathTarget);
    }
  }, 'any');

  // expression !filter "[" expression "]"
  case 'FilterExpression': return tag((context) => {

    const target = args[0](context);

    const filterFn = args[2];

    const filterTarget = isArray(target) ? target : [ target ];

    // null[..]
    if (target === null) {
      return null;
    }

    const type = filterFn.type;

    // a[1]
    // a[true]
    // a[b]
    // a[b()]
    // a[1 + 3]
    if (FILTER_INDEX_TYPES.has(type)) {
      const idx = filterFn(context);

      if (isBoolean(idx)) {
        if (idx === true) {
          return target;
        } else {
          return [];
        }
      }

      if (!isNumber(idx)) {
        return [];
      }

      const value = filterTarget[idx < 0 ? filterTarget.length + idx : idx - 1];

      if (typeof value === 'undefined') {
        return null;
      } else {
        return value;
      }
    }

    // TODO(nikku): not covered by spec
    // a["attr"]
    if (type === 'string') {

      const value = filterFn(context);

      return filterTarget.filter(el => el === value);
    }

    // a[b=c]
    // a[>10]
    if (type === 'test') {

      return filterTarget.map(el => {

        const iterationContext = {
          ...context,
          item: el,
          ...el
        };

        let result = filterFn(iterationContext);

        // test is fn(val) => boolean SimpleUnaryTest
        if (typeof result === 'function') {
          result = result(el);
        }

        if (isRange(result)) {
          result = result.includes(el);
        }

        if (result === true) {
          return el;
        }

        return result;
      }).filter(isTruthy);
    }

    // a[null]
    // a[@"2026-12-12"]
    return null;
  }, 'any');

  case 'SimplePositiveUnaryTest': return tag((context) => {

    // <Interval>
    if (args.length === 1) {
      return args[0](context);
    }

    // <CompareOp> <Expr>
    return args[0](context)(args[1](context));
  }, 'test');

  case 'List': {

    const elements = args.slice(1, -1);

    return (context) => {
      return elements.map(arg => arg(context));
    };
  }

  // "[" endpoint ".." endpoint "]"
  case 'Interval': {

    const startBracket = args[0] === '[';
    const endBracket = args[4] === ']';

    return tag((context) => {

      const left = args[1](context);
      const right = args[3](context);

      const startIncluded = left !== null && startBracket;
      const endIncluded = right !== null && endBracket;

      return createRange(left, right, startIncluded, endIncluded);
    }, 'test');
  }

  case 'PositiveUnaryTests':
  case 'Expressions': return (context) => {
    return args.map(a => a(context));
  };

  case 'Expression': return (context) => {
    return args[0](context);
  };

  case 'UnaryTests': {

    const negate = args[0] === 'not';

    const tests = negate ? args.slice(2, -1) : args;

    return (context) => {

      return (value = null) => {

        const matches = tests.map(test => test(context)).flat(1).map(test => {

          if (isArray(test)) {
            return test.includes(value);
          }

          if (typeof test === 'boolean') {
            return test;
          }

          return compareValue(test, value);
        }).some(v => v === true);

        return matches === null ? null : (negate ? !matches : matches);
      };
    };
  }

  default: return node.name;
  }
}

function getBuiltin(name, _context) {
  return getFromContext(name, builtins);
}

function extractValue(context, prop, _target) {

  const target = _target(context);

  if (LIST_OR_RANGE.has(getType(target))) {
    return target.map(t => (
      { [prop]: t }
    ));
  }

  return null;
}

function compareIn(value, tests) {

  if (!isArray(tests)) {

    if (getType(tests) === 'nil') {
      return null;
    }

    tests = [ tests ];
  }

  // three-valued membership: a matching test wins, otherwise any
  // incomparable (null) test makes the whole result unknown (null)
  let unknown = false;

  for (const test of tests) {
    let result = compareValue(test, value);

    // scalar equality is two-valued in a membership test: an
    // incomparable (null) comparison of plain values means not equal
    if (result === null && !isRange(test) && typeof test !== 'function') {
      result = false;
    }

    if (result === true) {
      return true;
    }

    if (result === null) {
      unknown = true;
    }
  }

  return unknown ? null : false;
}

function compareValue(test, value) {

  if (typeof test === 'function') {
    return test(value);
  }

  if (isRange(test)) {
    return test.includes(value);
  }

  return equals(test, value);
}


function coalecenseTypes(a, b) {

  if (!b) {
    return a.type;
  }

  if (a.type === b.type) {
    return a.type;
  }

  return 'any';
}

type ContextFn<T> = (context: InterpreterContext) => T;
type TaggedFn = {
  type: string,
  constantValue?: unknown
};

function tag<Z, T extends ContextFn<Z>>(fn: T, type: string) : T & TaggedFn {

  return Object.assign(fn, {
    type,
    toString() {
      return `TaggedFunction[${type}] ${Function.prototype.toString.call(fn)}`;
    }
  });
}

/**
 * A tagged evaluation function for a value known at build time; the
 * `constantValue` marker lets enclosing nodes (e.g. AtLiteral) fold
 * themselves at build time, too.
 */
function constant<Z>(value: Z, type: string) : ContextFn<Z> & TaggedFn {

  return Object.assign(tag(() => value, type), {
    constantValue: value
  });
}

/**
 * Whether an evaluation function carries a build-time constant value.
 */
function isConstant(fn: ContextFn<unknown> & TaggedFn) : boolean {
  return 'constantValue' in fn;
}

function isTruthy(obj) {
  return obj !== false && obj !== null;
}


// instance of //////////////////////////////////////////////////////

/**
 * A parsed FEEL type, as it appears on the right-hand side of an
 * `instance of` expression.
 *
 * @typedef {(
 *   { kind: 'name', name: string, resolve?: (context) => any } |
 *   { kind: 'list', element: TypeDescriptor } |
 *   { kind: 'context', entries: { name: string, type: TypeDescriptor }[] } |
 *   { kind: 'function' }
 * )} TypeDescriptor
 */

const anyType = (value) => getType(value) !== 'nil';

/**
 * Predicates for the named FEEL types, all built on the {@link getType}
 * oracle (the single source of truth for type identity). The two duration
 * variants additionally inspect the duration's kind.
 */
const NAMED_TYPE_CHECKS = {
  'Any': anyType,
  'Null': (value) => value === null,
  'number': (value) => getType(value) === 'number',
  'string': (value) => getType(value) === 'string',
  'boolean': (value) => getType(value) === 'boolean',
  'date': (value) => getType(value) === 'date',
  'time': (value) => getType(value) === 'time',
  'date and time': (value) => getType(value) === 'date time',
  'duration': (value) => getType(value) === 'duration',
  'days and time duration': (value) => isDuration(value) && !value.yearsMonths,
  'years and months duration': (value) => isDuration(value) && value.yearsMonths,
  'context': (value) => getType(value) === 'context',
  'list': (value) => getType(value) === 'list',
  'range': (value) => getType(value) === 'range',
  'function': (value) => getType(value) === 'function'
};

function normalizeTypeName(name: string) : string {
  return name.replace(/\s+/g, ' ').trim();
}

function isTypeDescriptor(obj) : boolean {
  return obj && typeof obj === 'object' && typeof obj.kind === 'string';
}

/**
 * Whether `value` is an instance of the given FEEL type, per the DMN
 * `instance of` operator. Composite types (`list<T>`, `context<...>`) are
 * matched structurally; named types delegate to {@link getType}, and an
 * unknown name falls back to a JavaScript constructor resolved from the
 * evaluation context.
 */
function matchesType(value, descriptor, context) : boolean {

  if (!isTypeDescriptor(descriptor)) {
    return false;
  }

  switch (descriptor.kind) {
  case 'name': {
    const check = NAMED_TYPE_CHECKS[descriptor.name];

    if (check) {
      return check(value);
    }

    const target = descriptor.resolve ? descriptor.resolve(context) : null;

    return typeof target === 'function' && value instanceof target;
  }
  case 'list':
    return getType(value) === 'list' && value.every(
      element => matchesType(element, descriptor.element, context)
    );
  case 'context':
    return getType(value) === 'context' && descriptor.entries.every(
      ({ name, type }) => has(value, name) && matchesType(value[name], type, context)
    );
  case 'function':
    return getType(value) === 'function';
  }

  return false;
}

function parseString(str: string) {

  if (str.startsWith('"')) {
    str = str.slice(1);
  }

  if (str.endsWith('"')) {
    str = str.slice(0, -1);
  }

  return str.replace(/(\\")|(\\\\)|(\\n)|(\\r)|(\\t)|(\\u[a-fA-F0-9]{5,6})|((?:\\u[a-fA-F0-9]{1,4})+)/ig, function(substring: string, ...groups: any[]) {

    const [
      quotes,
      backslash,
      newline,
      carriageReturn,
      tab,
      codePoint,
      charCodes
    ] = groups;

    if (quotes) {
      return '"';
    }

    if (newline) {
      return '\n';
    }

    if (carriageReturn) {
      return '\r';
    }

    if (tab) {
      return '\t';
    }

    if (backslash) {
      return '\\';
    }

    const escapePattern = /\\u([a-fA-F0-9]+)/ig;

    if (codePoint) {
      const codePointMatch = escapePattern.exec(codePoint);

      return String.fromCodePoint(parseInt(codePointMatch[1], 16));
    }

    if (charCodes) {
      const chars = [];

      let charCodeMatch;

      while ((charCodeMatch = escapePattern.exec(substring)) !== null) {
        chars.push(parseInt(charCodeMatch[1], 16));
      }

      return String.fromCharCode(...chars);
    }

    throw new Error('illegal match');
  });
}


type LintError = {
  message: string
};

function lintErrorDetails(errorNodeRef: SyntaxNodeRef) : {
  message: string,
  position: SourceLocation
} {

  const node = errorNodeRef.node;
  const parent = node.parent;

  const {
    from,
    to
  } = node;

  if (node.from !== node.to) {
    return {
      message: `Unrecognized token in <${parent.name}>`,
      position: {
        from,
        to
      }
    };
  }

  const next = findNext(node);

  if (next) {
    return {
      message: `Unrecognized token <${next.name}> in <${parent.name}>`,
      position: {
        from: next.from,
        to: next.to
      }
    };
  } else {
    const unfinished = parent.enterUnfinishedNodesBefore(errorNodeRef.to);

    return {
      message: `Incomplete <${ (unfinished || parent).name }>`,
      position: {
        from,
        to
      }
    };
  }
}

function lintError(input: string, errorNodeRef: SyntaxNodeRef): LintError {

  const {
    message,
    position
  } = lintErrorDetails(errorNodeRef);

  return new SyntaxError(
    message,
    {
      input: input.slice(position.from, position.to),
      position
    }
  );
}

function findNext(nodeRef: SyntaxNodeRef) : SyntaxNode | null {

  const node = nodeRef.node;

  let next, parent = node;

  do {
    next = parent.nextSibling;

    if (next) {
      return next;
    }

    parent = parent.parent;
  } while (parent);

  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatValue(value: any) {
  const type = getType(value);

  if (type === 'string') {
    return `"${ String(value) }"`;
  }

  if (type === 'list') {
    return `[${value.length} items]`;
  }

  if (type === 'context') {
    return '{...}';
  }

  if (type === 'function') {

    const parameterNames = value.parameterNames;

    if (parameterNames) {
      return `function(${ parameterNames.join(', ') })`;
    }

    return 'function';
  }

  if (type === 'nil') {
    return 'null';
  }

  return String(value);
};